import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Upload, Download, Eye, Trash2, Loader2, File, FileText, Image, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { it as itLocale, enUS as enLocale } from "date-fns/locale";
import { useLanguage } from "../../context/LanguageContext";
import { localStrings } from "../../i18n/homeStrings";
import { canPreview } from "../../lib/filePreview";

// Files shown before the list is folded away behind a button.
//
// Shortening the rows was not the answer on its own: a list with no limit is
// still a list with no limit, and thirty short rows push the page down just as
// forty tall ones did. Five is what a project's files look like when somebody
// opens it to read the timeline - enough to see what is there, few enough that
// the panel stays beside the timeline rather than running past it.
const COLLAPSED_COUNT = 5;

// Past this many, the expanded list scrolls inside its own box. Opening "show
// all" on a project with eighty files should not turn the page into a mile of
// column either.
const SCROLL_AFTER = 12;

// Past this many, files appear at once rather than one after another.
// staggerChildren delays each item by a fixed step, so an effect that looks
// considered with five files means the twentieth arrives two seconds after the
// list opens - indistinguishable, to whoever is waiting, from slowness.
const MAX_STAGGERED = 15;

function getFileIcon(contentType) {
  if (contentType?.startsWith("image/")) return Image;
  if (contentType === "application/pdf") return FileText;
  return File;
}

// The extension, read off the name. The icon only tells a PDF from an image:
// a .docx, a .zip and a .step all arrive at the same generic sheet of paper,
// so the one thing somebody scanning the list wants to know - what kind of
// file this is - was the thing the row did not say.
//
// Read from the filename rather than the content type because that is what the
// person who uploaded it recognises: "XLSX", not
// "vnd.openxmlformats-officedocument.spreadsheetml.sheet".
function fileExtension(name) {
  const dot = String(name || "").lastIndexOf(".");
  if (dot < 1) return null;
  const ext = name.slice(dot + 1);
  // Guards against a dot late in a name that is not an extension.
  if (!ext || ext.length > 5 || !/^[A-Za-z0-9]+$/.test(ext)) return null;
  return ext.toUpperCase();
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0 },
};

export function FileSection({
  files, fileInputRef, handleFileUpload, handleFileDelete,
  handleDownload, handlePreview, uploading, dragging,
  handleDragOver, handleDragLeave, handleDrop, t,
}) {
  const { language } = useLanguage();
  const s = localStrings(language);

  // The file pending deletion, or null. Deleting used to happen on the click
  // itself: one tap removed a file from storage for everybody, permanently,
  // with nothing in between. The same action on the Files page has always
  // asked first.
  const [pendingDelete, setPendingDelete] = useState(null);

  // Folded by default. Somebody opening a project is usually there to read the
  // timeline, not to go through the files; the ones who want a file ask for
  // them.
  const [expanded, setExpanded] = useState(false);

  const uploadedOn = (iso) => {
    try {
      return format(new Date(iso), "d MMM", {
        locale: language === "it" ? itLocale : enLocale,
      });
    } catch {
      return "";
    }
  };

  const foldable = files.length > COLLAPSED_COUNT;
  const visible = expanded || !foldable ? files : files.slice(0, COLLAPSED_COUNT);
  const scrolls = expanded && files.length > SCROLL_AFTER;

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: visible.length > MAX_STAGGERED ? 0 : 0.06 },
    },
  };

  return (
    <div>
      {/* The count belongs in the heading once the list can be folded: without
          it there is no way to tell five files from fifty. */}
      <h2 className="text-lg font-semibold text-slate-900 mb-4">
        {t("project.files")}
        {files.length > 0 && (
          <span className="ml-2 text-sm font-normal text-slate-400 tabular-nums">
            {files.length}
          </span>
        )}
      </h2>

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`dropzone cursor-pointer mb-4 ${dragging ? "dragging" : ""}`}
        data-testid="file-upload-zone"
      >
        <input ref={fileInputRef} type="file" className="hidden" onChange={(e) => handleFileUpload(e.target.files)} />
        {uploading ? (
          <Loader2 className="w-6 h-6 mx-auto text-indigo-600 animate-spin" />
        ) : (
          <>
            <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-500">{t("project.dropUpload")}</p>
          </>
        )}
      </div>

      {/* pr-1 keeps the scrollbar off the cards rather than over them. */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className={`space-y-2 ${scrolls ? "max-h-[30rem] overflow-y-auto pr-1" : ""}`}
      >
        {visible.map((file) => {
          const FileIcon = getFileIcon(file.content_type);
          const ext = fileExtension(file.original_filename);
          return (
            <motion.div
              key={file.id}
              variants={itemVariants}
              data-testid={`file-item-${file.id}`}
              className="file-item rounded-lg px-3 py-2.5 group shadow-sm"
            >
              <div className="flex items-start gap-3">
                <FileIcon className="w-5 h-5 text-cyan-500 shrink-0 mt-0.5" />

                <div className="flex-1 min-w-0">
                  {/* Two lines rather than one truncated one. At this column
                      width a real filename does not fit on a single line, so
                      truncating meant the list showed the beginning of every
                      name and the whole of none - and two files from the same
                      set look identical when the part that differs is the part
                      cut off. title carries the exact name for the few that
                      still overflow. */}
                  <p
                    className="text-sm font-medium text-slate-700 leading-snug line-clamp-2 break-words"
                    title={file.original_filename}
                  >
                    {file.original_filename}
                  </p>
                  <div className="flex items-center gap-1.5 mt-1 min-w-0">
                    {ext && (
                      <span className="shrink-0 text-[10px] font-semibold tracking-wide text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
                        {ext}
                      </span>
                    )}
                    <span className="text-xs text-slate-400 truncate">
                      {formatFileSize(file.size)} &bull; {uploadedOn(file.created_at)} &bull; {file.uploaded_by_name}
                    </span>
                  </div>
                </div>

                {/* Always visible, not revealed on hover: a phone has no hover
                    and these are the only way to open a file. */}
                <div className="flex items-center gap-0.5 shrink-0">
                  {canPreview(file.content_type) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`preview-file-${file.id}`}
                      onClick={() => handlePreview(file)}
                      aria-label={t("file.view")}
                      title={t("file.view")}
                      className="h-8 w-8 text-slate-400 hover:text-indigo-700"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid={`download-file-${file.id}`}
                    onClick={() => handleDownload(file)}
                    aria-label={t("file.download")}
                    title={t("file.download")}
                    className="h-8 w-8 text-slate-400 hover:text-indigo-700"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid={`delete-file-${file.id}`}
                    onClick={() => setPendingDelete(file)}
                    aria-label={s("files.delete")}
                    title={s("files.delete")}
                    className="h-8 w-8 text-slate-300 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          );
        })}
        {files.length === 0 && (
          <p className="text-center text-sm text-slate-400 py-4">{t("project.noFiles")}</p>
        )}
      </motion.div>

      {foldable && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          data-testid="toggle-all-files"
          className="w-full mt-2 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium text-slate-500 hover:text-indigo-700 hover:bg-slate-50 transition-colors"
        >
          {expanded ? t("file.showLess") : `${t("file.showAll")} (${files.length})`}
          <ChevronDown
            className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => { if (!v) setPendingDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{s("files.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {s("files.deleteDesc", { name: pendingDelete?.original_filename || "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-200">{s("files.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const id = pendingDelete?.id;
                setPendingDelete(null);
                if (id) handleFileDelete(id);
              }}
              data-testid="confirm-delete-file"
              className="bg-red-600 hover:bg-red-700"
            >
              {s("files.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
